import { TextareaHTMLAttributes, forwardRef, useRef, useEffect } from 'react';
import clsx from 'clsx';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  autoResize?: boolean;
  showCharCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = true,
      autoResize = false,
      showCharCount = false,
      className,
      id,
      disabled,
      maxLength,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const textareaId = id || props.name || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;

    // Auto-resize functionality
    useEffect(() => {
      if (autoResize && textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize, textareaRef]);

    const currentLength = value?.toString().length || 0;

    return (
      <div className={clsx('flex flex-col', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={textareaId}
            className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
            {props.required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}

        <textarea
          ref={textareaRef}
          id={textareaId}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          onChange={onChange}
          className={clsx(
            // Base styles
            'block w-full rounded-lg border shadow-sm',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed',
            // Padding
            'px-3 py-2',
            // Min height
            'min-h-[100px]',
            // Resize
            autoResize ? 'resize-none overflow-hidden' : 'resize-y',
            // States
            hasError
              ? 'border-red-300 text-red-900 dark:text-red-400 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 dark:bg-gray-800 placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500',
            // Custom className
            className
          )}
          {...props}
        />

        <div className="flex justify-between items-start mt-1.5">
          <div className="flex-1">
            {(error || helperText) && (
              <p
                className={clsx(
                  'text-sm',
                  hasError ? 'text-red-600' : 'text-gray-500'
                )}
              >
                {error || helperText}
              </p>
            )}
          </div>

          {showCharCount && maxLength && (
            <p className={clsx(
              'text-sm ml-2',
              currentLength > maxLength ? 'text-red-600' : 'text-gray-500'
            )}>
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
